import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import type { EntityType } from '@/types/watchlist';
import {
  ENTITY_TYPE_I18N_KEY,
  entityTypeRoutes,
  ENTITY_TYPE_WATCHLIST_VIEW_ID,
} from './watchlistViews';

interface WatchlistItemProps {
  entityType: EntityType;
  count: number;
  onDelete: () => void;
  onNavigate?: () => void; // 导航前的回调，用于关闭 Popover 等
}

/**
 * Watchlist 列表项组件
 * 显示实体类型、数量，悬停时显示删除图标
 *
 * 点击 → 跳到对应 source 的列表页 + 自动选中 "My Watchlist" system view。
 */
export function WatchlistItem({ entityType, count, onDelete, onNavigate }: WatchlistItemProps) {
  const { t } = useTranslation('chrome');
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const entityName = t(`watchlist.entityType.${ENTITY_TYPE_I18N_KEY[entityType]}`);

  const handleClick = () => {
    if (onNavigate) {
      onNavigate();
    }

    const route = entityTypeRoutes[entityType];
    if (!route) {
      console.warn('[WatchlistItem] No route found for entityType', entityType);
      return;
    }

    const viewId = ENTITY_TYPE_WATCHLIST_VIEW_ID[entityType];
    const target = viewId ? `${route}?viewId=${viewId}` : route;

    // 使用 setTimeout 延迟导航,确保 Popover 先关闭
    setTimeout(() => {
      void navigate(target);
    }, 100);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div
      className="group flex items-center justify-between px-3 py-2 hover:bg-accent rounded-md cursor-pointer transition-colors"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
    >
      <div className="flex items-center gap-2 flex-1">
        <span className="text-sm font-medium">{entityName}</span>
        {!isHovered && (
          <Badge variant="secondary" className="text-xs">
            {count}
          </Badge>
        )}
      </div>
      {isHovered && (
        <button
          onClick={handleDeleteClick}
          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 rounded transition-colors"
          aria-label={t('watchlist.deleteAriaLabel', { name: entityName })}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
